import { Cartesian } from "./graphing";
import {
  Config,
  Gaps,
  Image,
  Option,
  Options,
  Prompt,
  Scoring,
  Spot,
  Text,
  Token,
} from "./item-stem";

export enum InteractionTypeEnum {
  Choice = "choice",
  ChoiceMulti = "choice-multiple",
  ChoiceSingle = "choice-single",
  TextExtended = "text-extended",
  TextExtendedShortReading = "text-extended-scr-reading",
  TextExtendedShortWriting = "text-extended-scr-writing",
  TextExtendedEssay = "text-extended-essay",
  TextInline = "text-inline",
  TextInlineEntry = "text-inline-entry",
  TextInlineMathEntry = "text-inline-math-entry",
  TextInlineNumericEntry = "text-inline-numeric-entry",
  TextInlineChoice = "text-inline-choice",
  TextSelectable = "text-selectable",
  TextGapMatch = "text-gap-match",
  Order = "sequencing",
  Match = "match",
  MatchTable = "match-table",
  Associate = "associate",
  Drawing = "drawing",
  GraphicGapMatch = "graphic-gap-match",
  GraphicHotspot = "graphic-hotspot",
  GraphicAssociate = "graphic-associate",
  GraphicOrder = "graphic-order",
  GraphicSelectPoint = "graphic-select-point",
  GraphicPositionObject = "graphic-position-object",
  Audio = "audio",
  Gridded = "gridded",
  FractionModel = "fraction-model",
  FractionModelConstruct = "fraction-model-construct",
  CartesianInequality = "cartesian-inequality",
  Slider = "slider",
  NumberLinePlot = "number-line-plot",
  CartesianGraph = "cartesian-graph",
  LineChart = "line-chart",
  BarChart = "bar-chart",
}

export interface StemContentBase {
  prompt: Prompt;
}

export interface StemContentBaseGraphic extends StemContentBase {
  image?: Image;
  spots?: Array<Spot>;
}
export interface StemBase {
  id: string;
  type: "stem";
  config?: Partial<Config>;
  scoring?: Partial<Scoring>;
  language?: string;
}

export type StemFractionModel = StemBase & {
  interactionType: InteractionTypeEnum.FractionModel;
  content: StemContentBase;
  config: FractionModelConfig;
};

export type StemFractionModelConstruct = StemBase & {
  interactionType: InteractionTypeEnum.FractionModelConstruct;
  content: StemContentBase;
  config: FractionModelConfig;
};

export type StemSlider = StemBase & {
  interactionType: InteractionTypeEnum.Slider;
  content: StemContentBase;
  config: SliderConfig;
};

export type StemMatchTable = StemBase & {
  interactionType: InteractionTypeEnum.MatchTable;
  content: StemContentBase & {
    optionsTitle?: Text;
    options: Options;
    stems: Options;
  };
};

export type StemGridded = StemBase & {
  interactionType: InteractionTypeEnum.Gridded;
  content: StemContentBase;
  config: ConfigGridded;
};

export type StemCartesianGraph = StemBase & {
  interactionType: InteractionTypeEnum.CartesianGraph;
  content: StemContentBase;
  config: ConfigCartesianGraph;
};

export type StemCartesianInequality = StemBase & {
  interactionType: InteractionTypeEnum.CartesianInequality;
  content: StemContentBase;
  config: ConfigCartesianGraph;
};

export type StemNumberLinePlot = StemBase & {
  interactionType: InteractionTypeEnum.NumberLinePlot;
  content: StemContentBase;
  config: ConfigNumberLine;
};

export type StemGraphicGapMatch = StemBase & {
  interactionType: InteractionTypeEnum.GraphicGapMatch;
  content: StemContentBaseGraphic & {
    options?: Options;
    tokens?: Array<Token>;
    gaps?: Gaps;
  };
};

export type StemGraphicHotspot = StemBase & {
  interactionType: InteractionTypeEnum.GraphicHotspot;
  content: StemContentBaseGraphic;
};

export type StemGraphicAssociate = StemBase & {
  interactionType: InteractionTypeEnum.GraphicAssociate;
  content: StemContentBaseGraphic;
};

export type StemDrawing = StemBase & {
  interactionType: InteractionTypeEnum.Drawing;
  content: StemContentBaseGraphic & {
    tokens?: Token[];
  };
  config: ConfigDrawing;
};

export type StemGraphicOrder = StemBase & {
  interactionType: InteractionTypeEnum.GraphicOrder;
  content: StemContentBaseGraphic;
};

export type StemGraphicSelectPoint = StemBase & {
  interactionType: InteractionTypeEnum.GraphicSelectPoint;
  content: StemContentBaseGraphic;
};

export type StemGraphicPositionObject = StemBase & {
  interactionType: InteractionTypeEnum.GraphicPositionObject;
  content: StemContentBaseGraphic & {
    tokens?: Token[];
  };
};

export type StemChoice = StemBase & {
  interactionType: InteractionTypeEnum.Choice;
  content: StemContentBase & {
    options: Options;
  };
};

export type StemAssociate = StemBase & {
  interactionType: InteractionTypeEnum.Associate;
  content: StemContentBase & {
    options: Options;
  };
};

export type StemMatch = StemBase & {
  interactionType: InteractionTypeEnum.Match;
  content: StemContentBase & {
    optionsOne: Array<Option>;
    optionsTwo: Array<Option>;
  };
};

export type StemOrder = StemBase & {
  interactionType: InteractionTypeEnum.Order;
  content: StemContentBase & {
    options: Options;
  };
};

export type StemTextExtended = StemBase & {
  interactionType: InteractionTypeEnum.TextExtended;
  content: StemContentBase & {
    prompt: Prompt;
  };
};

export type StemTextInline = StemBase & {
  interactionType: InteractionTypeEnum.TextInline;
  content: StemContentBase & {
    text: Partial<Text>;
    gaps: Gaps;
  };
};

export type StemTextGapMatch = StemBase & {
  interactionType: InteractionTypeEnum.TextGapMatch;
  content: StemContentBase & {
    text: Partial<Text>;
    gaps: Gaps;
    options: Options;
  };
};

export type StemTextSelectable = StemBase & {
  interactionType: InteractionTypeEnum.TextSelectable;
  content: StemContentBase & {
    text: Partial<Text>;
    options: Options;
  };
};

export type StemAudio = StemBase & {
  interactionType: InteractionTypeEnum.Audio;
  content: StemContentBase;
};

export type Stem = StemBase &
  (
    | StemAssociate
    | StemAudio
    | StemBarChart
    | StemCartesianGraph
    | StemChoice
    | StemFractionModel
    | StemFractionModelConstruct
    | StemGraphicAssociate
    | StemDrawing
    | StemGraphicGapMatch
    | StemGraphicHotspot
    | StemCartesianInequality
    | StemGraphicOrder
    | StemGraphicPositionObject
    | StemGraphicSelectPoint
    | StemGridded
    | StemLineChart
    | StemMatch
    | StemMatchTable
    | StemNumberLinePlot
    | StemOrder
    | StemSlider
    | StemTextExtended
    | StemTextGapMatch
    | StemTextInline
    | StemTextSelectable
  );

export interface ConfigGridded extends Partial<Config> {
  layout: {
    columnCount: number;
    hasPlusMinusColumn?: boolean;
    hasFixedDecimal?: boolean;
    hasFractionSlash?: boolean;
    hasFloatingDecimal?: boolean;
    decimalColumn?: number;
    rangeMin: number;
    rangeMax: number;
  };
}

export interface ConfigCartesianGraph extends Partial<Config> {
  graph: Cartesian.Graph;
}

export interface ConfigDrawing extends Partial<Config> {
  responseArea?: {
    width?: number;
    height?: number;
  };
}

export interface ConfigNumberLine extends Partial<Config> {
  numberLine: Cartesian.NumberLine;
}

export interface FractionModelConfig extends Partial<Config> {
  model?: {
    shape: "pie" | "bar";
    rows: string;
    columns: string;
    groups?: string;
    slices: string;
    caption?: string;
  };
}

export interface SliderConfig extends Partial<Config> {
  slider: {
    min?: string;
    max?: string;
    step?: string;
  };
}

export type ChartData = {
  label: string;
  value: number;
}[];

export type StemChart = {
  config: ConfigCartesianGraph;
  content: StemContentBase & {
    data: ChartData;
  };
  scoring: {
    data: {
      op: string;
      value: number;
    }[];
  };
};

export type StemBarChart = StemBase &
  StemChart & {
    interactionType: InteractionTypeEnum.BarChart;
  };

export type StemLineChart = StemBase &
  StemChart & {
    interactionType: InteractionTypeEnum.LineChart;
  };
